/**
 * UspBanner — dismissal for the header USP banner.
 *
 * The close button hides the host and records that in `sessionStorage`, so the banner stays closed
 * for the rest of the visit instead of returning on the next page view. Hiding sets the `hidden`
 * attribute, which the section stylesheet keys off twice: to take the banner out of the layout,
 * and — via `body:has(.usp-banner:not([hidden]))` — to drop `--announcement-bar-offset` back to 0
 * so the header pill slides up into the space.
 *
 * The restore on load is NOT here: a custom element upgrades after first paint, so a returning
 * visitor would see the banner flash and the header pill jump. `header-announcements.liquid` does
 * that synchronously inline, right after the markup, and both sides read the same
 * `data-storage-key` so the key is defined in one place.
 *
 * DOM contract:
 *   [data-usp-banner-close] — the close button
 *   [data-storage-key]      — sessionStorage key shared with the inline restore script
 */

class UspBanner extends HTMLElement {
  connectedCallback() {
    this.#closeButton?.addEventListener("click", this.#dismiss);
  }

  disconnectedCallback() {
    this.#closeButton?.removeEventListener("click", this.#dismiss);
  }

  get #closeButton() {
    return /** @type {HTMLElement | null} */ (this.querySelector("[data-usp-banner-close]"));
  }

  #dismiss = () => {
    this.hidden = true;

    const key = this.dataset.storageKey;
    if (!key) return;

    try {
      sessionStorage.setItem(key, "1");
    } catch {
      // Private browsing or a full quota — the banner still closes for this page view.
    }
  };
}

if (!customElements.get("usp-banner")) {
  customElements.define("usp-banner", UspBanner);
}
